import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RegisterSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    if (attempts >= 12) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/register/verify-payment?session_id=${sessionId}`);
      if (response.data.status === 'success') {
        setStatus('success');
        return;
      }
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    } catch (err) {
      if (attempts < 5) {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="register-success-checking">
        <div className="text-center">
          <Loader2 className="w-14 h-14 text-[#E8613A] animate-spin mx-auto mb-5" />
          <h2 className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Bekräftar betalning...</h2>
          <p className="text-sm text-[#9B9490] mt-2">Vänligen vänta ett ögonblick</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4" data-testid="register-success-done">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-[#E5E0DA] p-8 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-9 h-9 text-green-500" />
          </div>
          <img src="/operio-logo-2.png" alt="Operio" className="h-8 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Välkommen till Operio!
          </h1>
          <p className="text-sm text-[#9B9490] mt-2 mb-1">
            Din prenumeration är aktiverad med 14 dagars fri provperiod.
          </p>
          <p className="text-xs text-[#9B9490] mb-6">
            Nästa steg: verifiera ditt företag för att låsa upp fakturering och löner.
          </p>
          <Button onClick={() => navigate('/dashboard')} data-testid="register-success-goto-dashboard"
            className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white h-11 font-semibold">
            Kom igång
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4" data-testid="register-success-error">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-[#E5E0DA] p-8 text-center">
        <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <AlertCircle className="w-9 h-9 text-amber-500" />
        </div>
        <h1 className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {status === 'timeout' ? 'Betalningen tar längre tid' : 'Något gick fel'}
        </h1>
        <p className="text-sm text-[#9B9490] mt-2 mb-6">
          {status === 'timeout'
            ? 'Din betalning behandlas fortfarande. Du kan kontrollera status i inställningar.'
            : 'Kunde inte bekräfta betalningen. Kontakta support om problemet kvarstår.'
          }
        </p>
        <div className="space-y-2">
          <Button onClick={() => navigate('/dashboard')} className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white h-11">
            Gå till Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/register')} className="w-full h-11">
            Försök igen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegisterSuccessPage;
